import {
  Stack,
  Container,
} from '@mui/material';
import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { base64url, compactDecrypt } from 'jose';
import { verify } from '@turgid-tributary/smart-health-card-decoder';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import PatientCard from '../components/PatientCard';
import networkConfig from '../config/networkConfig';
import { PSContext, SHLContext } from '../Contexts';
import { safeFetch } from '../utils/dataUtil';
import { RoutingPaths } from '../constants';
import PageLayout from './PageLayout';

/**
 * Layout for Patient Summary landing and section pages. Fetches and provies PS data.
 */
const PSLayout = () => {
  const { files, encryptionKey } = useContext(SHLContext);
  const [i18n] = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [ps, setPS] = useState(null);

  const { showBoundary } = useErrorBoundary();

  const { shl } = useParams();

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      files.forEach(async (file) => {
        if (file.contentType === 'application/fhir+json') {
          try {
            const config = {
              method: 'GET',
              headers: {
                'X-VSF-API-TOKEN': networkConfig.apiKey,
                'Accept-Language': i18n.language,
              },
              signal: abortController.signal,
            };

            const response = await safeFetch(
              file.location,
              config,
              showBoundary,
            );
            const responseText = await response?.text();

            if (responseText) {
              const { plaintext } = await compactDecrypt(
                responseText,
                base64url.decode(encryptionKey),
              );
              const json = JSON.parse(new TextDecoder().decode(plaintext));
              if (json) {
                setPS(json);
              }
            }
          } finally {
            setIsLoading(false);
          }
        } else if (file.contentType === 'application/smart-health-card') {
          const config = {
            method: 'GET',
            headers: {
              'X-VSF-API-TOKEN': networkConfig.apiKey,
              'Accept-Language': i18n.language,
            },
            signal: abortController.signal,
          };

          const response = await safeFetch(
            file.location,
            config,
            showBoundary,
          );
          const responseText = await response?.text();

          if (responseText) {
            const { plaintext } = await compactDecrypt(
              responseText,
              base64url.decode(encryptionKey),
            );
            const json = JSON.parse(new TextDecoder().decode(plaintext));
            const shc = json.verifiableCredential[0];
            const result = await verify(shc);
            const { data: { fhirBundle } } = result;
            setPS(fhirBundle);
            setIsLoading(false);
          }
        }
      });
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <PSContext.Provider value={useMemo(() => ({ ps, shl }), [ps, shl])}>
      <PageLayout header={<PageHeader homeURL={`/${RoutingPaths.VIEWER}/${shl}?lang=${i18n.language}`} ps={ps} />}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(isLoading || !ps) && <LoadingSpinner />}
          {!isLoading && ps && (
            <Stack
              direction="column"
              alignItems="flex-start"
              padding={4}
              gap={2}
              sx={{ flexGrow: 1 }}
            >
              <PatientCard />
              <Outlet />
            </Stack>
          )}
        </Container>
      </PageLayout>
    </PSContext.Provider>
  );
};

export default PSLayout;
